import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import TableRow from "@mui/material/TableRow";
import { Pagination, TablePagination, TableSortLabel } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { Stack } from "@mui/system";
interface ObjectSectionItem {
  level: "Brand" | "Raw Table" | string;
  object_key: string | null | undefined;
  matching_id: string | number | null | undefined;
  object_value: string | null | undefined;
}

interface ObjectsProps {
  objectSectionData: ObjectSectionItem[];
  currentTableId: string | number | null | undefined;
}

const tableHeadings = {
  level: "Level",
  object_key: "Object Key",
  object_value: "Object Value",
};
const tableHeaders = [
  {
    id: 1,
    KEY: "level",
    label: "Level",
    sortable: true,
  },
  {
    id: 2,
    KEY: "object_key",
    label: "Object Key",
    sortable: true,
  },
  {
    id: 3,
    KEY: "object_value",
    label: "Object Value",
    sortable: true,
  },
];

const Objects = ({ objectSectionData, currentTableId }: ObjectsProps) => {
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [sort, setSort] = useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "level", direction: "desc" });

  const headCells = Object.entries(tableHeadings || {});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // FilterOut the ObjectSectionData based on the following filter:
  // - the level is "Brand" (always show if present)
  // - the level is "Raw Table" and only show rows with matching ID to the current raw table id
  const filteredObjectData = useMemo(() => {
    if (!objectSectionData?.length) return [];

    return objectSectionData.filter((item) => {
      const level = item?.level?.trim(); // handles empty string "  "

      if (level === "Brand") return true;

      if (level === "Raw Table") {
        const matchingId = item?.matching_id;
        // skip if matching_id is null, undefined, or empty string
        if (
          matchingId === null ||
          matchingId === undefined ||
          matchingId === ""
        )
          return false;
        return matchingId === currentTableId;
      }

      return false;
    });
  }, [objectSectionData, currentTableId]);

  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      // If no sort is applied, return original order
      if (!sort.keytoSort || !sort.direction) {
        return [...arrayToSort]; // Return a copy of original order
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined || keytoSort === null) return 0;

        let aValue;
        let bValue;

        aValue = a[keytoSort];
        bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort],
  );

  useEffect(() => {
    if (!filteredObjectData || filteredObjectData.length === 0) {
      setSortedData([]);
      return;
    }
    const sortedRows = getSortedData(filteredObjectData);
    setSortedData(sortedRows);
  }, [filteredObjectData, getSortedData]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    setSort({
      keytoSort: header.KEY,
      // Toggle sort direction if the same column is clicked, otherwise default to ascending
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  useEffect(() => {
    setPage(0);
  }, [currentTableId]);

  return (
    <>
      <TableContainer
        component={Paper}
        className="performanceTable"
        sx={{
          padding: "0px 0px",
          boxShadow: "none",
          borderStartStartRadius: "0px",
          maxHeight: 450,
          overflowX: "auto",
        }}
      >
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.KEY} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header?.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows?.length > 0 ? (
              paginatedRows.map((row: any, rowIndex: number) => (
                <React.Fragment key={rowIndex}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "level" ? (
                          <>{row[key] ? row[key] : ""} </>
                        ) : key === "object_key" ? (
                          <> {row[key] ? row[key] : ""}</>
                        ) : key === "object_value" ? (
                          <> {row[key] ? row[key] : ""}</>
                        ) : (
                          ""
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center">
                  No data found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {sortedData?.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
            }}
          />
          <Pagination
            count={Math.ceil(sortedData?.length / rowsPerPage)}
            page={page + 1}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      )}
    </>
  );
};

export default Objects;
